// src/components/headerone/bots/OmniBot2.js
// Omni bot 2.0 - context integration per OmniBots Website Context Integration Guide
import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const DEPLOYMENT_KEY = 'dep_web_N4oyqSOer835v3trt14TfFtv8TcPxlbQ';

// Map pathname to KB collection name (page_label). Must match Operations Portal collection names exactly.
// Collections (from portal): DMV, Education, GTSU, Emergency Management, Employment and Workforce Development,
//   Family and Children Services, Health and Human Services, Housing and Community Development.
// Fallback: "General FAQ" for pages without a specific collection.
function getPageLabel(pathname) {
  if (!pathname || pathname === '/') return 'General FAQ';
  const p = pathname.toLowerCase();
  // Department routes – match portal collection names (no "&", use "and")
  if (p.startsWith('/department/dmv')) return 'DMV';
  if (p.startsWith('/department/education/gtsu')) return 'GTSU';
  if (p.startsWith('/department/education')) return 'Education';
  if (p.startsWith('/department/emergency')) return 'Emergency Management';
  if (p.startsWith('/department/employment')) return 'Employment and Workforce Development';
  if (p.startsWith('/department/family-services')) return 'Family and Children Services';
  if (p.startsWith('/department/health')) return 'Health and Human Services';
  if (p.startsWith('/department/housing')) return 'Housing and Community Development';
  // Other routes – use General FAQ (no dedicated collection in portal)
  return 'General FAQ';
}

// Global flags to track script load / initialization
if (typeof window !== 'undefined') {
  if (typeof window.__omnibot2ScriptLoaded === 'undefined') {
    window.__omnibot2ScriptLoaded = false;
  }
  if (typeof window.__omnibot2Initialized === 'undefined') {
    window.__omnibot2Initialized = false;
  }
}

function OmniBot2({ userData, payload: cognitoPayload }) {
  const location = useLocation();

  // Build full context with every available piece of data (page, URL, user, browser, etc.)
  const getFullContextPayload = useCallback(() => {
    const w = typeof window !== 'undefined' ? window : null;
    const d = typeof document !== 'undefined' ? document : null;
    const page_label = getPageLabel(location.pathname);
    const page_url = w ? w.location.href : '';
    const pathname = location.pathname || '';
    const search = location.search || '';
    const hash = location.hash || '';
    // page_label (KB collection) is derived synchronously from the path via
    // getPageLabel, so it is always correct on navigation. page_title comes from the
    // live DOM title; the route-change effect below re-sends context after react-helmet
    // settles the title, so the bot still receives the correct page on SPA navigation.
    const page_title = d ? (d.title || '') : '';

    const ctx = {
      // Page / KB (required for dynamic KB)
      page_label,
      page_url,
      page_title,
      pathname,
      search,
      hash,
      // Navigation
      referrer: d?.referrer || '',
      // Browser / environment
      language: w?.navigator?.language || '',
      languages: w?.navigator?.languages ? String(w.navigator.languages) : '',
      timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
      screen_width: w?.screen?.width ?? '',
      screen_height: w?.screen?.height ?? '',
      // When this context was built
      context_updated_at: new Date().toISOString(),
    };

    // User (from userData – only include if present)
    if (userData) {
      if (userData.userId != null && userData.userId !== '') ctx.user_id_full = userData.userId;
      if (userData.customerID != null && userData.customerID !== '') ctx.user_id = userData.customerID;
      if (userData.fullName != null && userData.fullName !== '') ctx.user_name = userData.fullName;
      if (userData.firstName != null && userData.firstName !== '') ctx.first_name = userData.firstName;
      if (userData.lastName != null && userData.lastName !== '') ctx.last_name = userData.lastName;
      if (userData.emailAddress != null && userData.emailAddress !== '') ctx.user_email = userData.emailAddress;
      if (userData.accountNumber != null && userData.accountNumber !== '') ctx.account_number = userData.accountNumber;
    }

    // Documented context field: user_tier (subscription/tier for routing and dashboard)
    if (cognitoPayload && typeof cognitoPayload === 'object' && cognitoPayload['custom:user_tier'] != null) {
      ctx.user_tier = cognitoPayload['custom:user_tier'];
    } else if (userData?.userTier != null && userData.userTier !== '') {
      ctx.user_tier = userData.userTier;
    } else {
      ctx.user_tier = '';
    }

    // Safe Cognito payload attributes (scalars only, no tokens)
    if (cognitoPayload && typeof cognitoPayload === 'object') {
      if (cognitoPayload['cognito:username']) ctx.cognito_username = cognitoPayload['cognito:username'];
      if (cognitoPayload['custom:sms_optin'] != null) ctx.sms_optin = cognitoPayload['custom:sms_optin'];
    }

    return ctx;
  }, [location.pathname, location.search, location.hash, userData, cognitoPayload]);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.gtsActiveBot = 'omni2';
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.gtsActiveBot = 'omni2';
    }
  }, [location.pathname, location.search]);

  // Initialize widget with full context metadata (once)
  useEffect(() => {
    // If script is already loaded and we've initialized once, do NOT re-init;
    // route changes will be handled by the context update effect below.
    if (window.__omnibot2ScriptLoaded && window.__omnibot2Initialized) {
      return;
    }

    const cssLinkId = 'omnibot2-widget-css';
    if (!document.getElementById(cssLinkId)) {
      const link = document.createElement('link');
      link.id = cssLinkId;
      link.rel = 'stylesheet';
      link.href = 'https://widget.customer1.dev.omnibots.ai/widget.css';
      document.head.appendChild(link);
    }

    const scriptId = 'omnibot2-widget-js';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://widget.customer1.dev.omnibots.ai/widget.iife.js';
      script.async = true;
      script.onload = () => {
        window.__omnibot2ScriptLoaded = true;
        // Avoid double-init across route changes
        if (window.__omnibot2Initialized) return;

        const meta = getFullContextPayload();
        if (window.omnibot && typeof window.omnibot.init === 'function') {
          try {
            window.omnibot.init({
              deploymentKey: DEPLOYMENT_KEY,
              position: 'bottom-left',
              autoOpen: false,
              offset: { x: 20, y: 20 },
              zIndex: 9999,
              metadata: meta,
            });
            window.__omnibot2Initialized = true;
            console.log('[OMNI 2.0] Bot initialized (omnibot) – full payload sent:', meta);
            // Doc: subscribe to 'ready' and push context again so backend receives an explicit setContext update
            if (typeof window.omnibot.on === 'function') {
              window.omnibot.on('ready', () => {
                if (typeof window.omnibot.setContext === 'function') {
                  const payload = getFullContextPayload();
                  window.omnibot.setContext(payload);
                  console.log('[OMNI 2.0] Context sent on ready – full payload:', payload);
                }
              });
            }
          } catch (e) {
            console.error('[OMNI 2.0] Init error:', e);
          }
        }
      };
      script.onerror = () => console.error('[OMNI 2.0] Failed to load script');
      document.body.appendChild(script);
    } else {
      const existingScript = document.getElementById(scriptId);
      if (existingScript && !window.__omnibot2ScriptLoaded) {
        existingScript.onload = () => {
          window.__omnibot2ScriptLoaded = true;
          if (window.__omnibot2Initialized) return;

          const meta = getFullContextPayload();
          if (window.omnibot && typeof window.omnibot.init === 'function') {
            try {
              window.omnibot.init({
                deploymentKey: DEPLOYMENT_KEY,
                position: 'bottom-left',
                autoOpen: false,
                offset: { x: 20, y: 20 },
                zIndex: 9999,
                metadata: meta,
              });
              window.__omnibot2Initialized = true;
              if (typeof window.omnibot.on === 'function') {
                window.omnibot.on('ready', () => {
                  if (typeof window.omnibot.setContext === 'function') {
                    const p = getFullContextPayload();
                    window.omnibot.setContext(p);
                  }
                });
              }
            } catch (e) {
              console.error('[OMNI 2.0] Init error:', e);
            }
          }
        };
      }
    }
  }, [getFullContextPayload]);

  // SPA: update context on every URL change (pathname, search, or hash).
  // Send immediately with URL-derived context (correct even if document.title is
  // still stale), then re-send after the title settles to cover dynamic/unmapped
  // routes where getValidatedTitle falls back to document.title. Also fires when
  // userData/cognitoPayload changes (e.g. after login).
  useEffect(() => {
    if (!window.__omnibot2ScriptLoaded || !window.__omnibot2Initialized) return;

    let cancelled = false;
    const sendContext = (reason) => {
      if (cancelled) return;
      if (window.omnibot && typeof window.omnibot.setContext === 'function') {
        const payload = getFullContextPayload();
        try {
          window.omnibot.setContext(payload);
          console.log(`[OMNI 2.0] Context updated (${reason}) – full payload sent:`, payload);
        } catch (_) {}
      }
    };

    sendContext('route-change');
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => sendContext('after-frame')));
    const t1 = setTimeout(() => sendContext('after-title-350ms'), 350);
    const t2 = setTimeout(() => sendContext('after-title-900ms'), 900);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname, location.search, location.hash, getFullContextPayload]);

  // Keep widget visible on route change
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.gtsActiveBot = 'omni2';
      const ensureVisible = () => {
        ['[data-omnibot-widget]', '.omnibot-widget', '#omnibot-widget', 'iframe[src*="omnibots-widget"]'].forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => {
            if (el) {
              el.style.setProperty('display', 'block', 'important');
              el.style.setProperty('visibility', 'visible', 'important');
              el.style.setProperty('opacity', '1', 'important');
            }
          });
        });
      };
      ensureVisible();
      setTimeout(ensureVisible, 100);
      setTimeout(ensureVisible, 500);
    }
  }, [location.pathname, location.search]);

  return null;
}

export default OmniBot2;
